import { Fungicide } from '@cropx-common/models/CropZoneFungincidesModel';

export type CalendarEventsResponse = {
  content: CalendarEventsContent;
};
export type CalendarEventsContent = {
  events: CalendarEvents[];
  hasNextEvents: boolean;
  hasPreviousEvents: boolean;
};
export type CalendarEvents = {
  dayEvents: DayEvents[];
  date: number;
};
export type DayEvents = {
  day: number;
  events: CalendarEventInfo[];
};
export type CalendarEventInfo = {
  amount: number;
  nitrogenRate?: number;
  unitRate?: string;
  productRate?: number;
  unitProduct?: string;
  customCalenderEvent: CustomCalenderEvent;
  customCalenderEventId: number;
  eventId: number;
  eventTime: number;
  startTime?: number;
  endTime?: number;
  measurementUnitName: string;
  type: AgendaEventType;
  eventTextDescription: string;
  fertilizerId?: number;
  fertilizerName?: string;
  crop?: Crop;
  eventDetails?: EventDetails;
  fungicides?: Fungicide[];
  diseases?: string[];
  curative?: boolean;
  preventive?: boolean;
  notes?: string;
  objectNature?: AgendaEventTypeObjectNature;
  sections?: any[];
};

export enum AgendaEventType {
  ETService = 'ETService',
  ETServiceForecast = 'ETServiceForecast',
  IrrigationRecommendation = 'IrrigationRecommendation',
  RecommendationDate = 'RecommendationDate',
  FertilizerApplication = 'FertilizerApplication',
  RainfallService = 'RainfallService',
  RainfallServiceForecast = 'RainfallServiceForecast',
  RainfallWeatherStation = 'RainfallWeatherStation',
  PivotIrrigationAppliedService = 'PivotIrrigationApplyedService',
  IrrigationAsAppliedService = 'IrrigationAsAppliedService',
  Rainfall = 'Rainfall',
  Spray = 'Spray',
  IrrigationApplied = 'IrrigationApplyed',
  PlantingDate = 'PlantingDate',
  EmergenceDate = 'EmergenceDate',
  SensorInstallationDate = 'SensorInstallationDate',
  Custom = 'Custom',
  Scouting = 'Scouting',
  FlowMeters = 'FlowMeters',
  FertigationAsAppliedService = 'FertigationAsAppliedService',
  IrrigationRecommended = 'IrrigationRecommended',
}

export enum AgendaEventTypeObjectNature {
  TalgilAsAppliedIrrigation = 'TalgilAsAppliedIrrigation',
  WiseconnAsAppliedEvent = 'WiseconnAsAppliedEvent',
  PivotIrrigationAppliedEventCalendarEvent = 'PivotIrrigationAppliedEventCalendarEvent',
  CalendarEvent = 'CalendarEvent',
}

export type EventDetails = {
  description: string;
  id: number;
  measurements: EventMeasurement[];
};

export type EventMeasurement = {
  connectedInternalEntityId: number;
  connectedInternalEntityType: string;
  externalEntityName: string;
  externalEntityType: string;
  externalEntityUid: string;
  volume: number;
  volumePerArea: number;
  controllerName?: string;
  controllerSerial?: string;
};

export type CustomCalenderEvent = {
  description: string;
  id: number;
  name: string;
  unitType: UnitType;
  additionals: {
    name: AgendaEventType.Scouting | AgendaEventType.FlowMeters;
  };
};
export type UnitType = {
  description: string;
  id: number;
  name: string;
};
export type Crop = {
  cropTypeName: string;
  // This is the cropZone Id, not the Crop Id
  id: string;
  name: string;
};

export type CalendarEventInfoWithType = {
  typeInfo: CalendarEventType;
} & CalendarEventInfo;

export type CalendarEventResponse = {
  content: CalendarEventInfo;
};

export class CalendarEventType {
  static textCustomEventsIds = [2];

  static readonly IrrigationApplyed = new CalendarEventType(
    'IrrigationApplyed',
    'Irrigation',
    'irrigation',
    null,
    'cxi cxi-nutrition',
  );
  static readonly Rainfall = new CalendarEventType(
    'Rainfall',
    'Rainfall',
    'rainfall',
    'Manually Entered Rainfall',
    'cxi cxi-light-rain',
  );
  static readonly IrrigationRecommendation = new CalendarEventType(
    'IrrigationRecommendation',
    'Irrigation rec',
    'irrigation',
    null,
    'cxi cxi-droplets',
  );
  static readonly RainfallService = new CalendarEventType(
    'RainfallService',
    'Rainfall',
    'rainfall-service',
    'Rainfall Service',
    'cxi cxi-nutrition',
  );
  static readonly RainfallServiceForecast = new CalendarEventType(
    'RainfallServiceForecast',
    'Rain forecast',
    'rainfall-service',
    null,
    'cxi cxi-nutrition',
  );
  static readonly Custom = new CalendarEventType(
    'Custom',
    'Custom',
    'custom-event',
    null,
    'cxi cxi-nutrition',
  );

  static readonly PivotIrrigationApplyedService = new CalendarEventType(
    'PivotIrrigationApplyedService',
    'Irrigation service',
    'irrigation-applied-service',
  );
  static readonly PlantingDate = new CalendarEventType(
    'PlantingDate',
    `It's Planting time!`,
    'planting-event',
  );
  static readonly EmergenceDate = new CalendarEventType(
    'EmergenceDate',
    'Emergence',
    'emergence-date-event',
  );
  static readonly RecommendationDate = new CalendarEventType(
    'RecommendationDate',
    'Irrigation recommendation',
    'recommendation-date-event',
  );
  static readonly SensorInstallationDate = new CalendarEventType(
    'SensorInstallationDate',
    'Sensor Installed',
    'sensor-installation-date-event',
  );

  static readonly Spray = new CalendarEventType(
    'Spray',
    'Spray Event',
    'spray-event',
    null,
    'cxi cxi-spray',
  );

  static readonly RainfallWeatherStation = new CalendarEventType(
    'RainfallWeatherStation',
    'Weather Station Rainfall',
    'rainfall',
    'Weather Station Rainfall',
  );

  static readonly IrrigationAsAppliedService = new CalendarEventType(
    'IrrigationAsAppliedService',
    'Talgil as applied',
    'talgil-irrigation-event',
    null,
    'cxi cxi-valve text-purple-100',
  );

  static readonly FertilizerApplication = new CalendarEventType(
    'FertilizerApplication',
    'Fertilizer',
    'fertilizer-event',
    null,
    'cxi cxi-nutrition',
  );

  private constructor(
    private readonly key: string,
    public readonly description: string,
    public readonly className: string = 'default-style',
    public readonly alias?: string,
    public readonly iconClass?: string,
  ) {}

  static getType(type: string): CalendarEventType {
    let result: CalendarEventType = null;
    Object.entries(CalendarEventType).forEach((x) => {
      if (x[1] instanceof CalendarEventType && x[1].key === type) {
        result = x[1];
        return false;
      }
    });
    return result;
  }

  getKey(): string {
    return this.key;
  }

  isEditable(): boolean {
    const k = this.getKey();
    if (
      k === CalendarEventType.IrrigationApplyed.getKey() ||
      k === CalendarEventType.Custom.getKey() ||
      k === CalendarEventType.Rainfall.getKey() ||
      k === CalendarEventType.Spray.getKey() ||
      k === CalendarEventType.FertilizerApplication.getKey() ||
      k === CalendarEventType.PivotIrrigationApplyedService.getKey()
    ) {
      return true;
    }
    return false;
  }

  hasDetailView(): boolean {
    const k = this.getKey();
    if (k === CalendarEventType.IrrigationAsAppliedService.getKey()) {
      return true;
    }
    return false;
  }
}

export interface AgendaEvent extends CalendarEventInfo {
  description: string;
  displayValue: string;
}
